import { Injectable } from '@angular/core';
import type { QrStyle, QrStyleId, StickerSizeInches } from '../app.types';

type QrCodeModule = typeof import('qrcode');
type QrCodeModuleImport = QrCodeModule & { default?: QrCodeModule };

export type QrRenderOptions = {
  qrIconSize?: number;
  qrLabelText?: string;
  qrLabelSize?: number;
  logoImage?: HTMLImageElement | null;
};

@Injectable({ providedIn: 'root' })
export class QrRenderService {
  private qrCodeModulePromise?: Promise<QrCodeModule>;

  async renderPreviewToCanvas(
    canvas: HTMLCanvasElement,
    value: string,
    style: QrStyle,
    options: QrRenderOptions = {}
  ): Promise<string> {
    const qrIconSize = options.qrIconSize ?? 28;
    const logoImage = options.logoImage ?? null;

    const qrSize = 720;
    await this.renderQrOnlyToCanvas(canvas, value, style, { qrIconSize, logoImage }, qrSize);

    return canvas.toDataURL('image/png');
  }

  async qrImageDataUrl(
    value: string,
    style: QrStyle,
    options: QrRenderOptions = {},
    size = 320
  ): Promise<string> {
    const canvas = document.createElement('canvas');
    const qrIconSize = options.qrIconSize ?? 28;
    const logoImage = options.logoImage ?? null;

    const qrSize = size;
    await this.renderQrOnlyToCanvas(canvas, value, style, { qrIconSize, logoImage }, qrSize, 1);

    return canvas.toDataURL('image/png');
  }

  async qrStickerImageDataUrl(
    value: string,
    style: QrStyle,
    qrLabelText: string,
    qrIconSize: number,
    qrLabelSize: number,
    stickerSize: StickerSizeInches,
    logoImage: HTMLImageElement | null
  ): Promise<string> {
    const qrDataUrl = await this.qrImageDataUrl(value, style, { qrLabelText, qrIconSize, qrLabelSize, logoImage });
    const qrImage = await this.loadImage(qrDataUrl);
    const canvas = document.createElement('canvas');
    const size = Math.round(this.normalizedStickerSize(stickerSize) * 720);
    const context = canvas.getContext('2d');

    canvas.width = size;
    canvas.height = size;

    if (!context) {
      return qrDataUrl;
    }

    context.save();
    context.fillStyle = '#ffffff';
    context.beginPath();
    context.arc(size / 2, size / 2, size / 2 - 2, 0, Math.PI * 2);
    context.fill();
    context.clip();

    const qrSize = size * 0.72;
    const qrX = (size - qrSize) / 2;
    const qrY = (size - qrSize) / 2;
    context.drawImage(qrImage, qrX, qrY, qrSize, qrSize);

    context.restore();
    return canvas.toDataURL('image/png');
  }

  selectedQrStyle(styles: QrStyle[], styleId: QrStyleId): QrStyle {
    return styles.find((style) => style.id === styleId) || styles[0];
  }

  private async renderQrOnlyToCanvas(
    canvas: HTMLCanvasElement,
    value: string,
    style: QrStyle,
    options: { qrIconSize: number; logoImage: HTMLImageElement | null },
    size: number,
    margin = 2
  ): Promise<void> {
    const qrCode = await this.qrCodeModule();

    if (style.id === 'classic') {
      canvas.width = size;
      canvas.height = size;
      await qrCode.toCanvas(canvas, value, {
        errorCorrectionLevel: 'H',
        margin,
        width: size,
        color: {
          dark: style.dark,
          light: style.light
        }
      });
    } else {
      this.drawStyledQrCode(qrCode, canvas, value, style, options.qrIconSize, options.logoImage, size, margin);
    }

    if (!style.logoBackground) {
      const radius = size >= 720 ? 18 : 8;
      this.drawCenteredQrLogo(canvas, radius, options.qrIconSize, options.logoImage);
    }
  }

  private drawCenteredQrLogo(
    canvas: HTMLCanvasElement,
    radius: number,
    qrIconSize: number,
    logoImage: HTMLImageElement | null
  ): void {
    const context = canvas.getContext('2d');
    if (!context || !logoImage) {
      return;
    }

    const overlaySize = canvas.width * (this.normalizedQrIconSize(qrIconSize) / 100);
    const padding = Math.max(2, overlaySize * 0.08);
    const logoSize = overlaySize - padding * 2;
    const x = (canvas.width - overlaySize) / 2;
    const y = (canvas.height - overlaySize) / 2;

    context.fillStyle = '#ffffff';
    context.beginPath();
    context.roundRect(x, y, overlaySize, overlaySize, radius);
    context.fill();
    this.drawImageContained(context, logoImage, x + padding, y + padding, logoSize, logoSize);
  }

  private drawStyledQrCode(
    qrCodeModule: QrCodeModule,
    canvas: HTMLCanvasElement,
    value: string,
    style: QrStyle,
    qrIconSize: number,
    logoImage: HTMLImageElement | null,
    size: number,
    margin: number
  ): void {
    const qrCode = qrCodeModule.create(value, { errorCorrectionLevel: 'H' });
    const moduleCount = qrCode.modules.size;
    const cellSize = size / (moduleCount + margin * 2);
    const context = canvas.getContext('2d');

    if (!context) {
      return;
    }

    canvas.width = size;
    canvas.height = size;
    context.fillStyle = style.light;
    context.fillRect(0, 0, size, size);
    this.drawLogoBackground(context, style, size, qrIconSize, logoImage);

    const fillStyle = this.moduleFillStyle(context, style, size);
    context.fillStyle = fillStyle;

    for (let row = 0; row < moduleCount; row += 1) {
      for (let col = 0; col < moduleCount; col += 1) {
        if (!qrCode.modules.get(row, col)) {
          continue;
        }

        const x = (col + margin) * cellSize;
        const y = (row + margin) * cellSize;
        this.drawQrModule(context, style, x, y, cellSize);
      }
    }
  }

  private qrCodeModule(): Promise<QrCodeModule> {
    this.qrCodeModulePromise ??= import('qrcode').then((module: QrCodeModuleImport) => module.default ?? module);
    return this.qrCodeModulePromise;
  }

  private moduleFillStyle(context: CanvasRenderingContext2D, style: QrStyle, size: number): string | CanvasGradient {
    if (!style.accent) {
      return style.dark;
    }

    const gradient = context.createLinearGradient(0, 0, size, size);
    gradient.addColorStop(0, style.dark);
    gradient.addColorStop(1, style.accent);
    return gradient;
  }

  private drawLogoBackground(
    context: CanvasRenderingContext2D,
    style: QrStyle,
    size: number,
    qrIconSize: number,
    logoImage: HTMLImageElement | null
  ): void {
    if (!style.logoBackground || !logoImage) {
      return;
    }

    const scale = this.normalizedQrIconSize(qrIconSize) / 28;
    const logoSize = style.logoBackground === 'poster'
      ? size * Math.min(0.9, Math.max(0.3, 0.72 * scale))
      : size * Math.min(0.7, Math.max(0.2, 0.48 * scale));
    const x = (size - logoSize) / 2;
    const y = (size - logoSize) / 2;

    context.save();
    context.globalAlpha = style.logoBackground === 'poster' ? 0.34 : 0.18;
    this.drawImageContained(context, logoImage, x, y, logoSize, logoSize);
    context.restore();

    if (style.logoBackground === 'poster') {
      context.save();
      context.globalAlpha = 0.22;
      context.fillStyle = style.light;
      context.fillRect(0, 0, size, size);
      context.restore();
    }
  }

  private drawQrModule(context: CanvasRenderingContext2D, style: QrStyle, x: number, y: number, cellSize: number): void {
    if (style.shape === 'dot') {
      context.beginPath();
      context.arc(x + cellSize / 2, y + cellSize / 2, cellSize * 0.42, 0, Math.PI * 2);
      context.fill();
      return;
    }

    if (style.shape === 'rounded') {
      context.beginPath();
      context.roundRect(x + cellSize * 0.08, y + cellSize * 0.08, cellSize * 0.84, cellSize * 0.84, cellSize * 0.25);
      context.fill();
      return;
    }

    if (style.shape === 'diamond') {
      const inset = cellSize * 0.04;
      context.beginPath();
      context.moveTo(x + cellSize / 2, y + inset);
      context.lineTo(x + cellSize - inset, y + cellSize / 2);
      context.lineTo(x + cellSize / 2, y + cellSize - inset);
      context.lineTo(x + inset, y + cellSize / 2);
      context.closePath();
      context.fill();
      return;
    }

    if (style.shape === 'plus') {
      const arm = cellSize * 0.32;
      const center = cellSize * 0.34;
      context.fillRect(x + arm, y, center, cellSize);
      context.fillRect(x, y + arm, cellSize, center);
      return;
    }

    context.fillRect(x, y, cellSize, cellSize);
  }

  private loadImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = reject;
      image.src = src;
    });
  }

  private drawImageContained(
    context: CanvasRenderingContext2D,
    image: HTMLImageElement,
    x: number,
    y: number,
    width: number,
    height: number
  ): void {
    const imageWidth = image.naturalWidth || image.width;
    const imageHeight = image.naturalHeight || image.height;

    if (!imageWidth || !imageHeight) {
      return;
    }

    const scale = Math.min(width / imageWidth, height / imageHeight);
    const drawWidth = imageWidth * scale;
    const drawHeight = imageHeight * scale;
    const drawX = x + (width - drawWidth) / 2;
    const drawY = y + (height - drawHeight) / 2;

    context.drawImage(image, drawX, drawY, drawWidth, drawHeight);
  }

  private normalizedQrIconSize(value: number | string | null | undefined): number {
    const size = Number(value);
    if (!Number.isFinite(size)) {
      return 28;
    }

    return Math.min(32, Math.max(12, Math.round(size)));
  }

  private normalizedStickerSize(value: number | string): StickerSizeInches {
    if (value === '') {
      return 1;
    }

    const size = Number(value);
    if (!Number.isFinite(size)) {
      return 1;
    }

    return Math.min(2, Math.max(0.5, Math.round(size * 100) / 100));
  }
}
